import { Controller } from "@hotwired/stimulus"

// Inbox-style QA review. The list rail (left) stays mounted; clicking a row loads the evaluation's
// detail (transcript + scores, side by side) into the `qa-review-panel` turbo-frame in the center/right
// pane — the list is never destroyed, so filters/date/scroll are preserved. ArrowUp/ArrowDown page the
// rail; ArrowLeft/ArrowRight follow the Prev/Next links inside the loaded detail. The first row is
// auto-opened so reviewers land straight in the queue.
export default class extends Controller {
  static targets = ["frame", "results", "row"]

  static LOADING_HTML =
    '<div class="flex items-center justify-center h-full">' +
    '<div class="animate-pulse text-xs text-gray-400">Loading evaluation…</div></div>'

  static EMPTY_HTML =
    '<div class="flex items-center justify-center h-full text-sm text-gray-400">' +
    'No evaluations match these filters.</div>'

  connect() {
    this.onKeydown = this.onKeydown.bind(this)
    this.onFrameLoad = this.onFrameLoad.bind(this)
    this.onResultsLoad = this.onResultsLoad.bind(this)
    window.addEventListener("keydown", this.onKeydown)
    this.frameTarget.addEventListener("turbo:frame-load", this.onFrameLoad)
    if (this.hasResultsTarget) this.resultsTarget.addEventListener("turbo:frame-load", this.onResultsLoad)
    this.autoSelectFirst()
  }

  disconnect() {
    window.removeEventListener("keydown", this.onKeydown)
    this.frameTarget.removeEventListener("turbo:frame-load", this.onFrameLoad)
    if (this.hasResultsTarget) this.resultsTarget.removeEventListener("turbo:frame-load", this.onResultsLoad)
    this.exitKeyboardMode()
  }

  // Bound to a rail row click. Cmd/Ctrl/middle-click opens the inbox focused on this evaluation in a new tab.
  open(event) {
    if (event.button !== undefined && event.button !== 0 && event.button !== 1) return
    const url = event.params.url
    if (!url) return

    if (event.metaKey || event.ctrlKey || event.button === 1) {
      window.open(url, "_blank")
      return
    }

    event.preventDefault()
    this.selectRow(event.currentTarget)
  }

  load(url) {
    this.frameTarget.innerHTML = this.constructor.LOADING_HTML
    this.frameTarget.src = url
  }

  // Open a rail row's evaluation in the detail frame and mark it active.
  selectRow(row) {
    this.load(row.dataset.qaReviewPanelUrlParam)
    this.highlight(row)
  }

  autoSelectFirst() {
    if (this.frameTarget.getAttribute("src")) return
    const first = this.rowTargets[0]
    if (!first) return
    this.suppressReveal = true // initial load: leave the reviewer at the top (filters/list), not the detail
    this.selectRow(first)
  }

  get isDesktop() {
    return window.matchMedia("(min-width: 1024px)").matches // Tailwind `lg` — the fixed 3-pane breakpoint
  }

  highlight(row) {
    this.rowTargets.forEach((r) => r.classList.toggle("qa-rail-row--active", r === row))
    // Desktop: keep the active row visible in the rail's own scroll. On the stacked mobile layout the rail
    // and detail share one page scroll, so scrolling the row would yank the page away from the detail.
    if (this.isDesktop) row?.scrollIntoView({ block: "nearest" })
  }

  // After a detail (or a Prev/Next hop) loads, sync the rail highlight to the shown evaluation. If that
  // evaluation lives on another page (e.g. you paged past the end of the visible list), reload the rail
  // to the page holding it, then highlight once those rows arrive.
  onFrameLoad() {
    const detail = this.frameTarget.querySelector("[data-evaluation-id]")
    const id = detail?.dataset.evaluationId
    if (!id) return
    this.currentDetailId = id
    this.revealDetailOnMobile()

    const row = this.rowTargets.find((r) => r.dataset.evaluationId === id)
    // Not on the current page, or the row's cached invalidation state is stale after an invalidate/
    // restore — reload the rail to the page holding it and let onResultsLoad re-highlight. The detail is
    // already current here, so `pendingFollow` tells onResultsLoad the reload is a follow (re-highlight
    // the shown evaluation), not a filter change (reopen it).
    if (!row || row.dataset.invalidated !== detail.dataset.invalidated) {
      this.pendingFollow = true
      this.reloadRail(detail.dataset.qaPageUrl)
      return
    }
    this.highlight(row)
  }

  reloadRail(pageUrl) {
    if (pageUrl && this.hasResultsTarget) this.resultsTarget.src = pageUrl
  }

  // On the stacked mobile layout, bring the just-loaded detail into view after the reviewer opens a row
  // or pages Prev/Next — but not for automatic loads (initial auto-select or a filter/paginator reopen),
  // where they're looking at the filters/list. No-op on desktop (the panes are already both visible).
  revealDetailOnMobile() {
    const suppressed = this.suppressReveal
    this.suppressReveal = false
    if (suppressed || this.isDesktop) return
    this.frameTarget.scrollIntoView({ block: "start" })
  }

  onResultsLoad() {
    const following = this.pendingFollow
    this.pendingFollow = false
    const rows = this.rowTargets

    if (following) {
      // The rail is catching up to a detail that's already current (a Prev/Next hop, or a post-invalidate
      // refresh): just re-highlight its row, or drop the highlight if it fell out of the filtered list
      // (e.g. an evaluation you just invalidated leaving a needs-review view — the detail stays, so you
      // can still read/restore it).
      const row = rows.find((r) => r.dataset.evaluationId === this.currentDetailId)
      if (row) this.highlight(row)
      else this.clearHighlight()
      return
    }

    // The list itself changed (filter change or paginator click). Reopen the shown evaluation under the
    // NEW filters — so its Prev/Next, page URL and invalidate form all carry them — or the first row, or
    // nothing when the filters match no evaluations.
    if (!rows.length) return this.clearDetail()
    const row = (this.currentDetailId && rows.find((r) => r.dataset.evaluationId === this.currentDetailId)) || rows[0]
    this.suppressReveal = true // a filter/paginator reopen: keep the reviewer on the list, don't jump to the detail
    this.selectRow(row)
  }

  clearHighlight() {
    this.rowTargets.forEach((r) => r.classList.remove("qa-rail-row--active"))
  }

  clearDetail() {
    this.currentDetailId = null
    this.frameTarget.removeAttribute("src")
    this.frameTarget.innerHTML = this.constructor.EMPTY_HTML
  }

  onKeydown(event) {
    if (this.isTyping(event.target)) return
    if (["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "j", "k"].includes(event.key)) this.enterKeyboardMode()

    switch (event.key) {
      case "ArrowRight":
        this.clickNav("next", event)
        break
      case "ArrowLeft":
        this.clickNav("prev", event)
        break
      case "ArrowDown":
      case "j": // Gmail-style: next / down the list
        this.stepRow(1, event)
        break
      case "ArrowUp":
      case "k": // Gmail-style: previous / up the list
        this.stepRow(-1, event)
        break
    }
  }

  // While paging with the keyboard, suppress the row hover highlight (see .qa-rail-kbd CSS) so the
  // mouse-hovered row doesn't look selected alongside the actual active row. Cleared on next mouse move.
  enterKeyboardMode() {
    this.element.classList.add("qa-rail-kbd")
    if (this.exitKbd) return
    this.exitKbd = () => this.exitKeyboardMode()
    window.addEventListener("mousemove", this.exitKbd)
  }

  exitKeyboardMode() {
    this.element.classList.remove("qa-rail-kbd")
    if (!this.exitKbd) return
    window.removeEventListener("mousemove", this.exitKbd)
    this.exitKbd = null
  }

  // ←/→ follow the Prev/Next links rendered inside the detail (they carry the current filters).
  clickNav(direction, event) {
    const link = this.frameTarget.querySelector(`a[data-qa-nav="${direction}"]`)
    if (!link) return
    event.preventDefault()
    link.click()
  }

  // ↑/↓ move through the rail rows. Stepping past the current page's edge follows Prev/Next, which
  // crosses the page boundary (and the rail then reloads to that page) — just like ←/→.
  stepRow(delta, event) {
    const rows = this.rowTargets
    if (!rows.length) return
    const current = rows.findIndex((r) => r.classList.contains("qa-rail-row--active"))
    const target = current + delta

    if (current !== -1 && (target < 0 || target >= rows.length)) {
      this.clickNav(delta > 0 ? "next" : "prev", event)
      return
    }

    const next = rows[Math.min(Math.max(target, 0), rows.length - 1)]
    if (!next || next === rows[current]) return
    event.preventDefault()
    this.selectRow(next)
  }

  isTyping(el) {
    return el && el.closest("input, textarea, select, [contenteditable=true]")
  }
}
